import { useQuery } from "@tanstack/react-query";
import { type Task, type Client } from "@shared/schema";
import { PersonalTaskView } from "@/components/personal-task-view";
import { Loader2, AlertCircle } from "lucide-react";

// Interface pre team members v personal tasks komponente
interface TeamMember {
  id: number;
  invitedEmail: string;
  role: string;
  status: string;
}

export default function PersonalTasksPage() {
  const {
    data: tasks,
    isLoading: tasksLoading,
    error: tasksError,
  } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
    throwOnError: false,
    select: (data: any) => {
      const items = data?.items ?? data ?? [];
      return Array.isArray(items) ? items : [];
    },
  });

  const {
    data: clients,
    isLoading: clientsLoading,
    error: clientsError,
  } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    throwOnError: false,
    select: (data: any) => {
      return Array.isArray(data) ? data : [];
    },
  });

  const {
    data: teamMembers,
    isLoading: teamMembersLoading,
    error: teamMembersError,
  } = useQuery<TeamMember[]>({
    queryKey: ["/api/team-members"],
    retry: false, // Nebudeme opakovať ak nemáme oprávnenie
    throwOnError: false,
    select: (data: any) => {
      const items = data?.items ?? data ?? [];
      return Array.isArray(items) ? items : [];
    },
  });

  const isLoading = tasksLoading || clientsLoading || teamMembersLoading;
  // Ak je chyba 403 (Forbidden) pre team-members, ignorujeme ju
  const isForbiddenTeamMembers = teamMembersError && 
    (teamMembersError as any)?.message?.includes('Forbidden') || 
    (teamMembersError as any)?.message?.includes('403');
  
  const error = tasksError || clientsError || (teamMembersError && !isForbiddenTeamMembers);

  if (isLoading) {
    return (
      <div className="w-full flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-[400px] flex items-center justify-center">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <p>Chyba pri načítavaní údajov</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="section-spacing">
        <h1 className="page-title text-2xl sm:text-3xl font-bold mb-2">Osobný pohľad na úlohy</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          {isForbiddenTeamMembers 
            ? "Vaše priradené úlohy" 
            : "Vyberte člena tímu a pozrite si úlohy priradené tejto osobe"}
        </p>
      </div>
      
      <PersonalTaskView 
        teamMembers={teamMembers || []}
        tasks={tasks || []}
        clients={clients || []}
        showTeamSelector={!isForbiddenTeamMembers}
      />
    </div>
  );
}